import {createAsyncThunk, createReducer} from '@reduxjs/toolkit';

import {ENDPOINTS} from 'constants/endpoints';
import {AddToCartType} from 'desktop/pages/cart/types/quoteItem';
import {apiConfig, HTTP_REQUEST_HEADER} from 'libs/api/api';

import {ChangeItemQtyType} from './changeItemQtyTypes';

type NotificationCartType = 'Added' | 'Removed';

export type ItemChangeQtyType = {
    quoteItemId: string;
    qty: number;
    productId?: number | string;
    notificationType?: NotificationCartType;
};

const initialState: ChangeItemQtyType = {
    status: 'fulfilled',
};

/**
 * Change item qty in cart
 */
export const changeItemQtyAction = createAsyncThunk<
    {data: ItemChangeQtyType},
    ItemChangeQtyType,
    {
        fulfilledMeta: ItemChangeQtyType;
        rejectValue: {response: {data: string}};
    }
>(
    'CART@CHANGE_ITEM_QTY',
    async (
        {notificationType, productId, qty, quoteItemId},
        {fulfillWithValue, rejectWithValue},
    ) => {
        try {
            const response = await apiConfig.instanceGusaBackend({
                data: {qty},
                method: 'PUT',
                url: `${ENDPOINTS.CART_PUT_GET_QUOTE_ITEM_V2}/${quoteItemId}`,
            });
            return fulfillWithValue(response, {
                notificationType,
                productId,
                qty,
                quoteItemId,
            });
        } catch (e) {
            return rejectWithValue(e);
        }
    },
);

export const updateCartItem = createAsyncThunk<
    unknown,
    {data: AddToCartType; quoteType?: string},
    {fulfilledMeta: {quoteType: string}}
>('UPDATE_CART_ITEM', async ({data, quoteType = ''}, {fulfillWithValue}) => {
    const response = await apiConfig.instanceGusaBackend.request({
        data,
        headers: {
            [HTTP_REQUEST_HEADER.AUTHORIZATION]: '',
        },
        method: 'PUT',
        params: {
            ...(quoteType ? {quoteType} : {}),
        },
        url: `${ENDPOINTS.CART_PUT_GET_QUOTE_ITEM_V2}/${data.quoteItemId}`,
    });

    return fulfillWithValue(response, {quoteType});
});

export const updateCartItemSubscription = createAsyncThunk<unknown, AddToCartType>(
    'UPDATE_CART_ITEM_SUBSCRIPTION',
    data =>
        apiConfig.instanceGusaBackend.request({
            data,
            method: 'PUT',
            url: `${ENDPOINTS.CART_PUT_GET_QUOTE_ITEM_V2}/${data.quoteItemId}`,
        }),
);

/**
 * Change item quantity
 */
export const changeItemQty = createReducer<ChangeItemQtyType>(initialState, builder =>
    builder
        .addCase(changeItemQtyAction.fulfilled, (_, action) => ({
            ...action.payload.data,
            status: 'fulfilled',
        }))
        .addCase(changeItemQtyAction.pending, state => ({
            ...state,
            status: 'pending',
        }))
        .addCase(changeItemQtyAction.rejected, (state, {payload}) => ({
            ...state,
            error: payload?.response?.data,
            status: 'rejected',
        })),
);
